import { useEffect } from 'react';
import { useControl, useMap, type IControl } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { isEqual } from 'lodash-es';
import type { Feature, Polygon } from 'geojson';
import { AOI_DRAW_STYLES } from './draw-styles';

interface DrawAoiControlProps {
  /**
   * Controlled polygon (`null` = nothing drawn). Source of truth between
   * interactions and for external updates: changing it reconciles the drawing to
   * match (by value). It does not override an in-progress gesture, and the
   * control does not re-assert it on every render — the consumer is expected to
   * keep it in sync by storing `onChange`.
   */
  polygon: Polygon | null;
  /** Fires with the new polygon, or `null` when the user clears it. */
  onChange: (polygon: Polygon | null) => void;
  /**
   * Controlled vertex-editing flag. `true` puts the current polygon into `direct_select`;
   * `false` exits it. The polygon is also editable by clicking it directly on the map —
   * `onEditingChange` keeps this flag in sync with the map's actual mode.
   */
  editing?: boolean;
  /**
   * Fires as the draw mode changes — `true` on entering `direct_select`, `false` on
   * leaving it — so the consumer's `editing` mirrors both this control's writes and the
   * user's native map interactions (click a polygon to edit, click away to finish).
   */
  onEditingChange?: (editing: boolean) => void;
}

/**
 * Headless child of `<Map>` that draws/edits a single AOI polygon **without a toolbar**
 * — the polygon/trash buttons are hidden and drawing starts directly. Controlled: the
 * `polygon` prop is the source of truth, and draw events flow out through `onChange`
 * (`null` on clear). Five single-purpose effects:
 *
 * (1) Subscribe to draw events — in a dedicated effect (not `useControl`'s `onAdd`) so a
 *     changing `onChange` is always current.
 * (2) Sync the controlled `polygon` into the draw instance by value (skips no-ops and the
 *     echo of our own events, so it never disrupts an in-progress edit). Loads an existing
 *     polygon (e.g. on remount) for click-to-edit.
 * (3) Draw / re-arm: whenever there is no polygon, ensure `draw_polygon` mode — covering
 *     both the initial mount (draw directly) and re-arming after a delete. Runs only on a
 *     `polygon` identity change, so it never interrupts an in-progress draw (a freshly
 *     drawn-but-not-yet-committed polygon doesn't change `polygon`).
 * (4) Vertex editing: drive `direct_select` from the controlled `editing` flag.
 * (5) Mode mirror: report the map's actual draw mode back through `onEditingChange`, so
 *     `editing` stays accurate for native click-to-edit and clears when the user finishes.
 *
 * @param {DrawAoiControlProps} props - Controlled polygon + change callback + editing flag/callback.
 * @return {null} Always renders nothing.
 */
export function DrawAoiControl({
  polygon,
  onChange,
  editing = false,
  onEditingChange,
}: DrawAoiControlProps) {
  const { current: map } = useMap();

  const draw: MapboxDraw = useControl<IControl>(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        // No toolbar — drawing is started programmatically (effect 3) and managed from
        // the panel; the polygon/trash buttons are intentionally hidden.
        controls: { polygon: false, trash: false },
        styles: AOI_DRAW_STYLES,
      }) as unknown as IControl
  );

  // (1) Subscribe to draw events.
  useEffect(() => {
    if (!map) {
      return;
    }

    const handleChange = (event: { features: Feature[] }) => {
      // Single-AOI contract: the control only draws polygons, so anything else
      // is ignored rather than emitted.
      const geometry = event.features[0]?.geometry;
      if (geometry?.type === 'Polygon') {
        onChange(geometry);
      }
    };
    const handleDelete = () => onChange(null);

    map.on('draw.create' as string, handleChange);
    map.on('draw.update' as string, handleChange);
    map.on('draw.delete' as string, handleDelete);

    return () => {
      map.off('draw.create' as string, handleChange);
      map.off('draw.update' as string, handleChange);
      map.off('draw.delete' as string, handleDelete);
    };
  }, [map, onChange]);

  // (2) Sync the controlled polygon into the draw instance, comparing by VALUE
  // against what is actually drawn. This skips no-op writes and the echo of our
  // own draw events (the geometry is already present), regardless of whether the
  // consumer round-trips the exact object or a normalized copy — so it never
  // disrupts an in-progress edit.
  useEffect(() => {
    if (!draw) {
      return;
    }
    const drawn = draw.getAll().features[0]?.geometry ?? null;
    if (isEqual(drawn, polygon)) {
      return;
    }
    if (polygon) {
      draw.set({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: polygon, properties: {} }],
      });
    } else {
      draw.deleteAll();
    }
  }, [draw, polygon]);

  // (3) Draw / re-arm. With no polygon, make sure we're in draw mode so the user can
  // draw directly (mount) or draw a replacement (after a delete). Declared AFTER the
  // sync effect so a delete clears the feature (effect 2) before this re-arms. Runs
  // only on a `polygon` change, so a freshly drawn polygon — which leaves `polygon`
  // unchanged until the (possibly debounced) `onChange` commits — is never wiped.
  useEffect(() => {
    if (!draw) {
      return;
    }
    if (polygon === null && draw.getMode() !== 'draw_polygon') {
      draw.changeMode('draw_polygon');
    }
  }, [draw, polygon]);

  // (4) Vertex editing — drive the draw mode from the controlled `editing` flag. Enter
  // `direct_select` on the current feature when `editing` turns on; leave it when off.
  // Guarded on the current mode so re-runs are no-ops (and so the mode mirror below
  // doesn't ping-pong).
  useEffect(() => {
    if (!draw) {
      return;
    }
    if (editing) {
      const featureId = draw.getAll().features[0]?.id;
      if (featureId !== undefined && draw.getMode() !== 'direct_select') {
        draw.changeMode('direct_select', { featureId: String(featureId) });
      }
    } else if (draw.getMode() === 'direct_select') {
      draw.changeMode('simple_select');
    }
  }, [draw, editing]);

  // (5) Mode mirror — report editing state from the map's actual draw mode, so `editing`
  // reflects native click-to-edit and clears when the user clicks away (→ `simple_select`).
  useEffect(() => {
    if (!map || !onEditingChange) {
      return;
    }
    const handleModeChange = (event: { mode: string }) =>
      onEditingChange(event.mode === 'direct_select');
    map.on('draw.modechange' as string, handleModeChange);
    return () => {
      map.off('draw.modechange' as string, handleModeChange);
    };
  }, [map, onEditingChange]);

  return null;
}
